import React, { useEffect, useState } from "react";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import { Link, useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";
import { Formik } from "formik";
import * as Yup from "yup";
import { createBusinessParagraph, getBusinessParagraph, updateBusinessParagraph } from "../../../api/axios";
import CmsCircularProgress from "../../Common/CircularProgress";
import { showToast } from "../../../Utills/toast";
import FormGroup from "../../Common/FormGroup";

// Validation Schema
const validationSchema = Yup.object({
  paragraph1: Yup.string()
    .required("Part 1 is required"),
  paragraph2: Yup.string()
    .required("Part 2 is required"),
  paragraph3: Yup.string()
    .required("Part 3 is required"),
});

export default function ParagraphTwoEdit() {
    const { id, partnerId } = useParams();
    const navigate = useNavigate();
    const [paragraphTwo, setParagraphTwo] = useState({
      paragraph1:'',
      paragraph2:'',
      paragraph3:'',
      index: null,
    });
    const [saving, setSaving] = useState(false);
    const [isLoading, setIsLoading] = useState(true);

    const getInitialValues = () => ({
      paragraph1: paragraphTwo.paragraph1 || "",
      paragraph2: paragraphTwo.paragraph2 || "",
      paragraph3: paragraphTwo.paragraph3 || "",
    });

    const getBusinessParagraphById = async () => {
      try {
        const res = await getBusinessParagraph(id);
        const bottomData = res.data.data[0];
        setParagraphTwo(bottomData);
      } catch (error) {
        showToast({
          type: 'error',
          message: error?.response?.data?.message || "Failed to fetch paragraph data. Please try again.",
        });
      } finally {
        setIsLoading(false);
      }
    }

    useEffect(() => {
      setIsLoading(true);
      if (!Number(id)) {
        setIsLoading(false);
        return
      };
      getBusinessParagraphById(id);
    }, [id]);

    const handleSubmit = async (values, { setFieldValue }) => {
      setSaving(true);

      const formData = {
        paragraph1: values.paragraph1,
        paragraph2: values.paragraph2, 
        paragraph3: values.paragraph3,
      }

      if (!Number(id)) {
        try {
          formData.partner_id = partnerId;
          const response = await createBusinessParagraph(formData); 
          if(response.status === 200) {
            showToast({
              type: 'success',
              message: response?.data?.message || "Paragraph 2 section has been created successfully!",
            });
            const headerData = response?.data?.data;
            setParagraphTwo(headerData);
            if (headerData && headerData.id) {
              navigate(`/${partnerId}/forbusiness/headerparagraphtwo/edit/${headerData.id}`);
            }
          } else {
            showToast({
              type: 'error',
              message: response?.data?.message || "Failed to create paragraph 2 section. Please try again.",
            });
          }
        } catch (error) {
          showToast({
            type: 'error',
            message: error?.response?.data?.message || 'Failed to create paragraph 2 section. Please try again.',
          });
          console.log(error);
        }
      } else {
        try {
          const response = await updateBusinessParagraph(id, formData);          
          if(response.status === 200) {
            showToast({
              type: 'success',
              message: response?.data?.message || 'Paragraph 2 section has been updated successfully!',
            });
            
            // Update the form fields with the returned data if available
            const updatedParagraphData = response?.data?.data;
            if (updatedParagraphData) {
              setFieldValue("paragraph1", updatedParagraphData.paragraph1 || values.paragraph1);
              setFieldValue("paragraph2", updatedParagraphData.paragraph2 || values.paragraph2);
              setFieldValue("paragraph3", updatedParagraphData.paragraph3 || values.paragraph3);
              setParagraphTwo(prev => ({
                ...prev,
                ...updatedParagraphData,
              }));
            }
          } else {
            showToast({
              type: 'error',
              message: response?.data?.message || 'Failed to update paragraph 2 section. Please try again.',
            });
          }
        } catch (error) {
          console.log(error);
          showToast({
            type: 'error',
            message: error?.response?.data?.message || 'Failed to update paragraph 2 section. Please try again.',
          });
        }
      }
      
      setSaving(false);
    };

  return (
    <>
    {isLoading ? <CmsCircularProgress/> :
    <FormWrapper>
      <Formik
        initialValues={getInitialValues()}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize={true}
      >
        {({
          errors,
          touched,
          handleSubmit,
          isSubmitting,
          getFieldProps,
        }) => (
          <Form noValidate onSubmit={handleSubmit}>
            <Title>{Number(id) ? 'Edit' : 'Add' } Paragraph 2 Section</Title>
            <SliderDiv>
              <FormGroup 
                label='Enter a part 1'
                requiredField
                error={touched.paragraph1 && errors.paragraph1 ? errors.paragraph1 : ''}
              >
                <Form.Control
                  as="textarea"
                  rows={3}
                  placeholder="Enter part 1 content here..."
                  {...getFieldProps('paragraph1')}
                  isInvalid={touched.paragraph1 && !!errors.paragraph1}
                />
              </FormGroup>

              <FormGroup 
                label='Enter a part 2'
                requiredField
                error={touched.paragraph2 && errors.paragraph2 ? errors.paragraph2 : ''}
              >
                <Form.Control
                  as="textarea"
                  rows={3}
                  placeholder="Enter part 2 content here..."
                  {...getFieldProps('paragraph2')}
                  isInvalid={touched.paragraph2 && !!errors.paragraph2}
                />
              </FormGroup>

              <FormGroup 
                label='Enter a part 3'
                requiredField
                error={touched.paragraph3 && errors.paragraph3 ? errors.paragraph3 : ''}
              >
                <Form.Control
                  as="textarea"
                  rows={3}
                  placeholder="Enter part 3 content here..."
                  {...getFieldProps('paragraph3')}
                  isInvalid={touched.paragraph3 && !!errors.paragraph3}
                />
              </FormGroup>

              <ButtonWrapper className="mt-3">
                <Link to={`/${partnerId}/forbusiness/headerparagraphtwo/view/${Number(id) ? id : "create"}`}>
                  <Button variant="secondary" className="w-100">Go Back</Button>
                </Link>
                <Button
                  variant="primary"
                  type="submit"
                  disabled={saving || isSubmitting}
                  className="w-100"
                >
                  {saving ? "Saving..." : "Save Changes"}
                </Button>
              </ButtonWrapper>
            </SliderDiv>
          </Form>
        )}
      </Formik>
    </FormWrapper>}
    </>
  );
}

const FormWrapper = styled.div`
  @media (min-width:576px) {
   padding: 1rem;
  }
    @media (min-width:768px) {
   padding: 1.5rem;
  }
   padding: .5rem;
`;
const SliderDiv = styled.div`
  width: 100%;
  max-width: 800px;
  margin: 0 auto;
`;
const Title = styled.h3`
  text-align: center;
  padding: 10px 0;
`;

const ButtonWrapper = styled.div`
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 5rem;
  @media (max-width: 890px) {
    gap: 2rem;
  }
 @media (max-width: 425px) {
  gap: 1rem;
  }
`;
